"use client";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Image from "next/image";
import { useRef, MouseEvent } from "react";

const Photo = () => {
  const ref = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["15deg", "-15deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-15deg", "15deg"]);

  const cacheRect = () => {
    rectRef.current = ref.current?.getBoundingClientRect() ?? null;
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = rectRef.current;
    if (!rect) return;

    const mouseX = (e.clientX - rect.left) / rect.width - 0.5;
    const mouseY = (e.clientY - rect.top) / rect.height - 0.5;

    x.set(mouseX);
    y.set(mouseY);
  };

  const handleMouseLeave = () => {
    rectRef.current = null;
    x.set(0);
    y.set(0);
  };

  return (
    <div className="w-full h-full relative flex items-center justify-center">
      <motion.div
        ref={ref}
        className="w-[298px] h-[298px] xl:w-[min(340px,42vh)] xl:h-[min(340px,42vh)] rounded-full overflow-hidden flex items-center justify-center relative cursor-target"
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        whileHover={{
          scale: 1.05,
          transition: { duration: 0.3, ease: "easeOut" },
        }}
        whileTap={{
          scale: 0.98,
          transition: { duration: 0.1, ease: "easeOut" },
        }}
        onMouseEnter={cacheRect}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        initial={{ rotateX: 0, rotateY: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 30,
        }}
      >
        <Image
          src="/assets/parth-img.webp"
          width={640}
          height={640}
          // Matches w-[298px] / xl:w-[min(340px,42vh)] (Tailwind xl = 1280px)
          sizes="(max-width: 1279px) 298px, 340px"
          priority
          fetchPriority="high"
          quality={60}
          alt="Parth Kalani - Full Stack Developer"
          className="object-cover w-full h-full cursor-target"
          style={{
            transform: "translateZ(20px)",
          }}
        />

        {/* Animated border with enhanced 3D effect */}
        <motion.svg
          className="absolute top-0 left-0 w-full h-full pointer-events-none cursor-target"
          fill="transparent"
          viewBox="0 0 506 506"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: "translateZ(30px)",
          }}
        >
          <motion.circle
            cx="253"
            cy="253"
            r="250"
            stroke="#FEE715FF"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ strokeDasharray: "24 10 0 0" }}
            animate={{
              strokeDasharray: ["15 120 25 25", "16 25 92 72", "4 250 22 22"],
              rotate: [120, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        </motion.svg>

        {/* 3D depth indicator shadow */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: "linear-gradient(145deg, rgba(254, 231, 21, 0.1), rgba(254, 231, 21, 0.05))",
            transform: "translateZ(-10px)",
            filter: "blur(20px)",
          }}
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>
    </div>
  );
};

export default Photo;
